import { Button, FilterDropdownProps, Icons, Space } from "@pankod/refine-antd";
import { useTranslate } from "@pankod/refine-core";
import React, { useState } from "react";

export type AppFilterDropdownProps = FilterDropdownProps & {
    mapValue?: (selectedKeys: React.Key[]) => any;
};

/**
 * `<FilterDropdown>` is a helper component for {@link https://ant.design/components/table/#components-table-demo-custom-filter-panel filter dropdowns in Ant Design `<Table>` components.}
 *
 * @see {@link https://refine.dev/docs/ui-frameworks/antd/components/filter-dropdown} for more details.
 */
export const AppFilterDropdown: React.FC<AppFilterDropdownProps> = ({
    setSelectedKeys,
    confirm,
    clearFilters,
    mapValue,
    selectedKeys,
    children,
}) => {
    const [value, setValue] = useState<any[] | undefined>(selectedKeys);

    const clearFilter = () => {
        if (clearFilters) {
            setValue([]);
            clearFilters();
        }
        onFilter();
    };

    const onFilter = () => {
        if (confirm) confirm();
    };

    const mappedValue = (value: any) => (mapValue ? mapValue(value) : value);

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const onChange = (e: any) => {
        //console.log(e);
        if (typeof e === "object") {
            if (Array.isArray(e)) {
                const _mappedValue = mappedValue(e);

                setValue(_mappedValue);
                return setSelectedKeys(_mappedValue);
            }

            const { target }: React.ChangeEvent<HTMLInputElement> = e;
            const _mappedValue = mappedValue(target.value);
            setValue(_mappedValue);

            return setSelectedKeys(_mappedValue);
        }

        const _mappedValue = mappedValue(e);

        setValue(_mappedValue);
        return setSelectedKeys(_mappedValue);
    };

    const childrenWithProps = React.Children.map(children, (child) => {
        if (React.isValidElement(child)) {
            return React.cloneElement(child, {
                onChange,
                value: mappedValue(value),
                onKeyPress: (event: any) => {
                    if (event.key === 'Enter') {
                        onFilter();
                    }
                },
            });
        }
        return child;
    });
    const translate = useTranslate();
    return (
        <div
            style={{
                padding: 10,
                display: "flex",
                flexDirection: "column",
                alignItems: "flex-end",
            }}
        >
            <div style={{ marginBottom: 15 }}>{childrenWithProps}</div>
            <Space>
                <Button
                    type="primary" size="small" onClick={() => onFilter()}>
                    <Icons.FilterOutlined /> {translate("buttons.filter", "Filter")}
                </Button>
                <Button danger size="small" onClick={() => clearFilter()}>
                    {translate("buttons.clear", "Clear")}
                </Button>
            </Space>
        </div>
    );
};
